import { generarCeldas } from './divisionUtils';

const CELL = 18;          // ancho por columna de dígito
const ROW = 24;           // alto de cada fila (más espacio vertical entre cociente y dividendo)
const FONT = 17;
const STROKE = '#1a1a1a';
const SW = 2;           // stroke-width líneas estructura (cajón, techo, cociente)
const SW_OP = 1;          // stroke-width líneas de operación (restas entre pasos)
const STROKE_OP = '#bbb'; // color gris claro para líneas de operación

/**
 * Genera un SVG en blanco (solo estructura del cajón) con N columnas de dividendo.
 * Retorna { svgDataUrl, width, height, svg }
 */
export function divisionBlankToSvg(numColsDividendo = 3, numColsCociente = 3) {
  const totalCols = Math.max(numColsDividendo, numColsCociente);
  const divisorW = CELL * 2;
  const digitColsW = totalCols * CELL;
  const totalW = divisorW + digitColsW + 16;
  // 2 filas: cociente + dividendo (sin pasos)
  const totalH = 2 * ROW + 16;
  const PAD = 8;
  const xDigits = PAD + divisorW;

  const lines = [];

  // Pared derecha del cajón
  const xPared = PAD + divisorW;
  const yParedTop = PAD + ROW;
  const yParedBot = PAD + ROW * 2;
  lines.push(`<line x1="${xPared}" y1="${yParedTop}" x2="${xPared}" y2="${yParedBot}" stroke="${STROKE}" stroke-width="${SW}"/>`);

  // Techo del cajón
  const xTechoEnd = PAD + divisorW + totalCols * CELL;
  lines.push(`<line x1="${xPared}" y1="${yParedTop}" x2="${xTechoEnd}" y2="${yParedTop}" stroke="${STROKE}" stroke-width="${SW}"/>`);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">
  <rect width="${totalW}" height="${totalH}" fill="white"/>
  ${lines.join('\n  ')}
</svg>`;

  const encoded = encodeURIComponent(svg)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');

  return {
    svgDataUrl: `data:image/svg+xml,${encoded}`,
    width: totalW,
    height: totalH,
    svg,
  };
}

/**
 * Genera un SVG de "solo cajón": divisor + dividendo visibles, sin pasos ni cociente.
 * Retorna { svgDataUrl, width, height }
 */
export function divisionSoloCajonToSvg(dividendo, divisor) {
  const PAD = 8;
  const numCols = String(dividendo).length;
  const divisorW = Math.max(CELL, String(divisor).length * 8 + 6);
  const totalW = divisorW + numCols * CELL + 16;
  const totalH = 2 * ROW + 16;
  const xDigits = PAD + divisorW;
  const yDividendo = PAD + ROW * 1.5;

  const lines = [
    `<line x1="${PAD + divisorW}" y1="${PAD + ROW}" x2="${PAD + divisorW}" y2="${PAD + ROW * 2}" stroke="${STROKE}" stroke-width="${SW}"/>`,
    `<line x1="${PAD + divisorW}" y1="${PAD + ROW}" x2="${PAD + divisorW + numCols * CELL}" y2="${PAD + ROW}" stroke="${STROKE}" stroke-width="${SW}"/>`,
  ];
  const texts = [
    `<text x="${PAD + divisorW - 6}" y="${yDividendo}" text-anchor="end" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${STROKE}">${divisor}</text>`,
    ...String(dividendo).split('').map((d, i) =>
      `<text x="${xDigits + i * CELL + CELL / 2}" y="${yDividendo}" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${STROKE}">${d}</text>`
    ),
  ];

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">
  <rect width="${totalW}" height="${totalH}" fill="white"/>
  ${lines.join('\n  ')}
  ${texts.join('\n  ')}
</svg>`;

  const encoded = encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22');
  return { svgDataUrl: `data:image/svg+xml,${encoded}`, width: totalW, height: totalH };
}

/**
 * Genera un SVG como data URL directamente desde los datos de división,
 * sin necesitar renderizar React ni capturar el DOM.
 * Retorna { svgDataUrl, width, height }
 */
export function divisionToSvg(data, { conColores = false } = {}) {
  const COLOR_COCIENTE  = conColores ? '#c0392b' : STROKE;
  const COLOR_RESTA     = conColores ? '#2980b9' : STROKE;
  const COLOR_RESIDUO   = conColores ? '#27ae60' : STROKE;
  const COLOR_RASTRO    = '#aaaaaa'; // color del punto rastro (opacidad visual baja)

  // Opciones de visualización
  const simplificada       = !!data.simplificada;
  const sinDecimales       = !!data.sinDecimales;
  const operacionOriginal  = !!data.operacionOriginal; // muestra puntos rastro en posición original

  // Si sinDecimales, recalcular datos forzando sin decimales
  let renderData = data;
  if (sinDecimales && data.decimalesGenerados > 0) {
    // Recortar pasos decimales
    const pasosEnteros = data.pasos.filter(p => !p.esDecimal);
    const digitosCocienteEnteros = data.digitosCociente.slice(0, data.digitosCociente.length - data.decimalesGenerados);
    renderData = {
      ...data,
      pasos: pasosEnteros,
      digitosCociente: digitosCocienteEnteros,
      cociente: digitosCocienteEnteros.join(''),
      tieneDecimales: false,
      decimalesGenerados: 0,
      totalCols: data.numColsDividendo,
    };
  }

  const celdas = generarCeldas(renderData);
  if (!celdas) return null;

  const {
    filaCociente, filaDividendo, filaPasos,
    divisor: divisorRaw, totalCols, tieneDecimales, numColsDividendo,
  } = celdas;

  const decDividendo = renderData.decDividendo || 0;
  const decDivisor   = renderData.decDivisor   || 0;

  // Divisor siempre sin punto para el texto SVG; el punto rastro se superpone aparte
  const divisor = decDivisor > 0 ? divisorRaw.replace('.', '') : divisorRaw;

  // Columna tras la que va el punto rastro del dividendo
  // "34.5" → entero "345", numCols=3, dec=1 → punto después de col 1 (entre "34" y "5")
  const colPuntoDividendo = decDividendo > 0 ? numColsDividendo - decDividendo - 1 : -1;

  // En modo simplificada, filaPasos solo tiene filas de residuo (pares: resta vacía + residuo)
  // Construimos manualmente las filas visibles
  const filasVisibles = simplificada
    ? filaPasos.filter(f => !f.esResta)
    : filaPasos;

  // Usar divisorRaw para el ancho (siempre el más largo, evita salto de layout al togglear)
  const divisorW = Math.max(CELL, String(divisorRaw).length * 8 + 6);
  const puntW = tieneDecimales ? 8 : 0;
  // En operacionOriginal con punto en dividendo, el string original tiene 1 char extra (el punto)
  const extraColDividendo = (operacionOriginal && decDividendo > 0) ? CELL : 0;
  const totalW = divisorW + totalCols * CELL + puntW + extraColDividendo + 16;

  // Alto: cociente + dividendo + filas visibles
  // Cada fila de resta ocupa 1 ROW + línea debajo
  // Cada fila de residuo ocupa 1 ROW
  const totalH = (2 + filasVisibles.length) * ROW + 16;

  const PAD = 6;
  const xDigits = PAD + divisorW;

  function colX(col) {
    if (tieneDecimales && col >= numColsDividendo)
      return xDigits + numColsDividendo * CELL + puntW + (col - numColsDividendo) * CELL + CELL / 2;
    return xDigits + col * CELL + CELL / 2;
  }

  const lines = [];
  const texts = [];

  // ── Divisor ──
  const yDividendo = PAD + ROW * 1.5;
  if (operacionOriginal && decDivisor > 0) {
    // Dibujar carácter por carácter igual que divisionSoloCajonToSvg:
    // cada char ocupa 8px de ancho (mismo factor que divisorW = length * 8 + 6)
    const charW = 8;
    const chars = divisorRaw.split(''); // e.g. ['4', '.', '1']
    const totalCharsW = chars.length * charW;
    const xBase = PAD + divisorW - 4 - totalCharsW;
    chars.forEach((ch, i) => {
      const xCh = xBase + i * charW + charW / 2;
      const fill = ch === '.' ? COLOR_RASTRO : STROKE;
      const yText = ch === '.' ? yDividendo + FONT * 0.30 : yDividendo;
      const baseline = ch === '.' ? 'auto' : 'middle';
      const size = ch === '.' ? FONT + 1 : FONT;
      texts.push(`<text x="${xCh}" y="${yText}" text-anchor="middle" dominant-baseline="${baseline}" font-family="monospace" font-size="${size}" font-weight="700" fill="${fill}">${ch}</text>`);
    });
  } else {
    texts.push(`<text x="${PAD + divisorW - 4}" y="${yDividendo}" text-anchor="end" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${STROKE}">${divisor}</text>`);
  }

  // ── Cajón ──
  const xPared = PAD + divisorW;
  lines.push(`<line x1="${xPared}" y1="${PAD + ROW}" x2="${xPared}" y2="${PAD + ROW * 2}" stroke="${STROKE}" stroke-width="${SW}"/>`);
  lines.push(`<line x1="${xPared}" y1="${PAD + ROW}" x2="${PAD + divisorW + totalCols * CELL + puntW + extraColDividendo}" y2="${PAD + ROW}" stroke="${STROKE}" stroke-width="${SW}"/>`);

  // ── Cociente ──
  const yCociente = PAD + ROW * 0.5;
  filaCociente.forEach((celda, i) => {
    if (!celda.valor) return;
    const x = colX(i);
    texts.push(`<text x="${x}" y="${yCociente}" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${COLOR_COCIENTE}">${celda.valor}</text>`);
    const xLeft = tieneDecimales && i >= numColsDividendo
      ? xDigits + numColsDividendo * CELL + puntW + (i - numColsDividendo) * CELL
      : xDigits + i * CELL;
    lines.push(`<line x1="${xLeft}" y1="${PAD + ROW}" x2="${xLeft + CELL}" y2="${PAD + ROW}" stroke="${STROKE}" stroke-width="${SW}"/>`);
  });

  if (tieneDecimales) {
    const xPunto = xDigits + numColsDividendo * CELL + puntW / 2;
    texts.push(`<text x="${xPunto}" y="${yCociente + FONT * 0.30}" text-anchor="middle" dominant-baseline="auto" font-family="monospace" font-size="${FONT + 1}" font-weight="700" fill="${COLOR_COCIENTE}">.</text>`);
  }

  // ── Dividendo ──
  if (operacionOriginal && decDividendo > 0) {
    // Dibujar carácter por carácter desde el string original (e.g. "34.5")
    // El punto ocupa la misma celda CELL que un dígito — idéntico a divisionSoloCajonToSvg
    renderData.dividendo.split('').forEach((ch, i) => {
      const xCh = xDigits + i * CELL + CELL / 2;
      const fill = ch === '.' ? COLOR_RASTRO : STROKE;
      const yText = ch === '.' ? yDividendo + FONT * 0.30 : yDividendo;
      const baseline = ch === '.' ? 'auto' : 'middle';
      const size = ch === '.' ? FONT + 1 : FONT;
      texts.push(`<text x="${xCh}" y="${yText}" text-anchor="middle" dominant-baseline="${baseline}" font-family="monospace" font-size="${size}" font-weight="700" fill="${fill}">${ch}</text>`);
    });
  } else {
    filaDividendo.forEach((celda, i) => {
      if (!celda.valor) return;
      texts.push(`<text x="${colX(i)}" y="${yDividendo}" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${STROKE}">${celda.valor}</text>`);
    });
  }

  // ── Pasos ──
  // Cada paso ocupa 1 ROW de alto
  // Línea de resta va DEBAJO del número de resta
  let filaY = 2; // índice de fila actual (0=cociente, 1=dividendo, 2+=pasos)

  filasVisibles.forEach((fila) => {
    const yRow = PAD + filaY * ROW + ROW / 2;

    if (fila.esResta) {
      // Número de resta
      let firstCol = -1, lastCol = -1;
      fila.celdas.forEach((celda, ci) => {
        if (!celda.valor) return;
        if (firstCol < 0) firstCol = ci;
        lastCol = ci;
        texts.push(`<text x="${colX(ci)}" y="${yRow}" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${COLOR_RESTA}">${celda.valor}</text>`);
      });

      // Signo menos a la izquierda del primer dígito
      if (fila.tieneMenos && firstCol >= 0 && !renderData.sinMenos) {
        const xMenos = colX(firstCol) - CELL / 2 - 2;
        texts.push(`<text x="${xMenos}" y="${yRow}" text-anchor="end" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="700" fill="${COLOR_RESTA}">−</text>`);
      }

      // Línea DEBAJO del número de resta
      if (firstCol >= 0) {
        const yLine = PAD + (filaY + 1) * ROW; // debajo de la fila actual
        lines.push(`<line x1="${colX(firstCol) - CELL / 2}" y1="${yLine}" x2="${colX(lastCol) + CELL / 2}" y2="${yLine}" stroke="${STROKE_OP}" stroke-width="${SW_OP}"/>`);
      }
    } else {
      // Residuo — va debajo de la línea (ya en yRow que está centrado en la fila siguiente)
      fila.celdas.forEach((celda, ci) => {
        if (!celda.valor) return;
        texts.push(`<text x="${colX(ci)}" y="${yRow}" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="${FONT}" font-weight="600" fill="${COLOR_RESIDUO}">${celda.valor}</text>`);
      });
    }

    filaY++;
  });

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">
  <rect width="${totalW}" height="${totalH}" fill="white"/>
  ${lines.join('\n  ')}
  ${texts.join('\n  ')}
</svg>`;

  // Mismo encoding que NimbusMath usa para sus SVGs de MathJax
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');

  return {
    svgDataUrl: `data:image/svg+xml,${encoded}`,
    width: totalW,
    height: totalH,
    svg,
  };
}
